<template>
  <section>
    <UILoadingScreen
      v-if="fetchingEulas"
      :loading="true"
      loadingMessage="Loading EULAs..."
    />

    <section v-else class="subhead shadow-8">
      <span class="heading-1 text-center">EULA Admin </span>
    </section>

    <section v-if="!fetchingEulas" class="eula-container">
      <div id="eula-admin-wrapper">
        <div id="eula-list-wrapper">
          <div id="eula-left-section">
            <h3>Existing EULAs</h3>
            <div class="action-group">
              <Button size="small" severity="info" @click="handleCreateNewEula"
                >New</Button
              >
              <Button
                class="eula-export-btn"
                size="small"
                severity="success"
                :disabled="!activatedEula || exporting"
                @click="exportEulas"
              >
                Export
              </Button>
            </div>
          </div>
          <ul v-if="eulas.length" id="eula-list-items">
            <template v-for="eula in formattedEulas">
              <li
                v-if="!eula.isNew"
                :key="eula.id"
                class="eula-list-item"
                :class="{
                  'eula-selected': eula.selected,
                  'eula-activated': eula.activated
                }"
                @click="selectedEulaId = eula.id"
              >
                <a>
                  <span>
                    {{ eula.formattedCreatedDate }}
                  </span>
                </a>
                <Tag v-if="eula.activated" severity="danger" value="Active" />
              </li>
            </template>
          </ul>
          <span v-else>0 EULAs Found.</span>
        </div>

        <div id="selected-eula">
          <Message v-if="showNoValidEula" severity="info" :closable="false">
            Select "New" to add an End User License Agreement
          </Message>
          <Message v-if="!isOnline" severity="warn" :closable="false">
            Editing agreements is not available offline
          </Message>
          <Message
            v-if="
              !allowedToEditEula && !selectedEula?.activated && selectedEula
            "
            severity="warn"
            :closable="false"
          >
            Previously active EULAs cannot be edited or made active again
          </Message>
          <div v-if="selectedEula" class="selected-eula-actions">
            <div class="selected-eula-title">
              <strong>CREATED ON:</strong>
              {{ selectedEula.formattedCreatedDate }}
            </div>
            <div class="action-buttons">
              <Button
                v-if="
                  !isEditing &&
                  !isCreating &&
                  !selectedEula.isNew &&
                  !selectedEula.activated
                "
                size="small"
                :disabled="!canAlterSelectedEula"
                severity="danger"
                @click="handleActivateSelectedEula"
                @keyup.enter="handleActivateSelectedEula"
              >
                SET AS ACTIVE
              </Button>

              <Button
                v-if="selectedEula.activated"
                size="small"
                severity="danger"
                @click="handleDeactivateSelectedEula"
                @keyup.enter="handleDeactivateSelectedEula"
              >
                DEACTIVATE
              </Button>

              <Button
                v-if="
                  !selectedEula.isNew && !isEditing && !selectedEula.activated
                "
                :disabled="!canAlterSelectedEula"
                size="small"
                severity="info"
                @click="handleStartEditing"
                @keyup.enter="handleStartEditing"
              >
                EDIT
              </Button>

              <Button
                v-if="isEditing || isCreating"
                size="small"
                severity="danger"
                @click="handleCancel"
                @keyup.enter="handleCancel"
              >
                CANCEL
              </Button>

              <Button
                v-if="isEditing || isCreating"
                size="small"
                :disabled="isSaving || !selectedEulaHasChanges"
                severity="info"
                @click="handleSave"
                @keyup.enter="handleSave"
              >
                SAVE
              </Button>
            </div>
          </div>
          <Message
            v-if="formattedEulas.length > 0 && !selectedEula"
            severity="info"
            :closable="false"
          >
            Select a EULA to view or edit, or create the first one.
          </Message>
          <div id="selected-eula-license-wrapper">
            <Editor
              ref="editor"
              :readonly="!isEditing && !isCreating"
              :model-value="editingSelectedEulaLicense"
              @load="editorLoad"
              @update:model-value="
                (e: string) => (editingSelectedEulaLicense = e)
              "
            />
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import Editor from "primevue/editor";
import Message from "primevue/message";
import { computed, nextTick, ref, watch } from "vue";
import { useRouter } from "vue-router";
import useUserInfo from "@/composables/auth/useUserInfo";
import { removeArrayElement } from "@/utils/utils";
import { useConfirm } from "primevue/useconfirm";
import { useMutationNotification } from "@/composables/logger/mutationNotification";
import type { WorkBook, WorkSheet, RowInfo, Range } from "xlsx";
import type { Eula } from "gql/api";
import UILoadingScreen from "@/components/UILoadingScreen.vue";

// @ts-expect-error xlsx doesn't ship with declaration file
import XLSX from "xlsx/dist/xlsx.mini.min.js";

import { useOnline } from "@vueuse/core";

import {
  useInsertEulaMutation,
  useUpdateEulaLicenseMutation,
  useActivateEulaMutation,
  useDeactivateEulaMutation,
  useEulasSubSubscription,
  useGetAllEulaAcceptancesQuery
} from "gql/api";

type FormattedEula = Eula & {
  formattedCreatedDate: string;
  selected: boolean;
  saving: boolean;
  isNew: boolean;
};

const editor = ref();

const isOnline = useOnline();
const confirm = useConfirm();

const eulas = ref<Eula[]>([]);
const activatedEula = computed<Eula | null>(
  () => eulas.value?.find((eula: Eula) => eula.activated) || null
);

const { handleToastResponse } = useMutationNotification();
const fetchingEulas = ref(true);
const { data: eulaData } = useEulasSubSubscription();
const { executeMutation: insertEula } = useInsertEulaMutation();
const { executeMutation: updateEulaLicense } = useUpdateEulaLicenseMutation();
const { executeMutation: activateEula } = useActivateEulaMutation();
const { executeMutation: deactivateEula } = useDeactivateEulaMutation();

const dateTimeFormat = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric"
});

const {
  data: allUserEulaAcceptances,
  executeQuery: getAllUserEulaAcceptances
} = useGetAllEulaAcceptancesQuery({
  pause: true
});

watch(eulaData, cv => {
  if (cv) {
    fetchingEulas.value = false;
    eulas.value = cv?.eula || [];
  }
});

const user = useUserInfo();
const router = useRouter();

// redirect any non-admins away from the eula admin
if (!user.is_admin) {
  router.push("/");
}

const isEditing = ref(false);
const isCreating = ref(false);
const isSaving = ref(false);

const handleCreateNewEula = () => {
  isEditing.value = false;
  isCreating.value = true;
};

watch(isCreating, newValue => {
  if (newValue) {
    editingSelectedEulaLicense.value = "";
    selectedEulaId.value = CREATING_EULA_ID;
    nextTick(() => {
      editor.value?.quill.focus();
    });
  } else if (selectedEulaId.value === CREATING_EULA_ID) {
    selectedEulaId.value = activatedEula.value?.id || "";
  }
});

const showNoValidEula = computed(() => {
  if (isCreating.value) {
    return false;
  }
  return !eulas.value?.length;
});

const CREATING_EULA_ID = "CREATING_EULA_ID";

const savingEulaIds = ref<string[]>([]);
const selectedEulaId = ref<string>(activatedEula.value?.id || "");
const formattedEulas = computed<FormattedEula[]>(() => {
  const final =
    eulas.value?.map((e: Eula) => {
      return {
        ...e,
        selected: selectedEulaId.value === e.id,
        saving: savingEulaIds.value.includes(e.id),
        isNew: false,
        formattedCreatedDate: dateTimeFormat.format(new Date(e.created_at))
      };
    }) || [];

  final.sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  if (isCreating.value) {
    final.push({
      __typename: "eula",
      activated_on: null,
      activated: false,
      created_at: Date.now(),
      updated_at: Date.now(),
      deactivated_at: null,
      formattedCreatedDate: "Pending",
      id: CREATING_EULA_ID,
      isNew: true,
      license: editingSelectedEulaLicense.value,
      saving: false,
      selected: true,
      version: null
    });
  }

  return final;
});

const selectedEula = computed<FormattedEula | null>(
  () => formattedEulas.value.find(e => e.selected) || null
);

// work around to fix issue posted here - https://github.com/primefaces/primevue/issues/5606
const editorLoad = () => {
  const newValue =
    isEditing.value || isCreating.value
      ? editingSelectedEulaLicense.value
      : selectedEula.value?.license;
  const delta = editor.value.quill.clipboard.convert({
    html: newValue || ""
  });
  nextTick(() => editor.value.quill.setContents(delta));
};

watch([selectedEulaId, isEditing, isCreating], () => {
  editorLoad();
});

const editingSelectedEulaLicense = ref<string>(
  selectedEula.value?.license || ""
);

watch(selectedEulaId, newValue => {
  if (isEditing.value || (isCreating.value && newValue !== CREATING_EULA_ID)) {
    handleCancel();
  }
  editingSelectedEulaLicense.value = selectedEula.value?.license || "";
});

// this handles EULA that have previously been activated
const allowedToEditEula = computed(() => {
  if (selectedEula.value) {
    return (
      !selectedEula.value.activated_on && !selectedEula.value.deactivated_at
    );
  }
  return false;
});

const canAlterSelectedEula = computed(
  () =>
    allowedToEditEula.value &&
    !!selectedEula.value &&
    selectedEula.value.id !== activatedEula.value?.id &&
    !selectedEula.value.saving &&
    !savingActivatedEula.value
);

const selectedEulaHasChanges = computed(
  () =>
    allowedToEditEula.value &&
    !!selectedEula.value &&
    (selectedEula.value.license !== editingSelectedEulaLicense.value ||
      isCreating.value)
);

const savingActivatedEula = ref(false);

const canUpdateSelectedEula = computed(
  () =>
    selectedEula.value &&
    !savingEulaIds.value.includes(selectedEula.value.id) &&
    selectedEulaHasChanges
);

const handleUpdateSelectedEula = () => {
  if (!canUpdateSelectedEula.value) {
    return;
  }
  const savingId = selectedEulaId.value;
  const savingLicense = editingSelectedEulaLicense.value;

  savingEulaIds.value.push(savingId);
  updateEulaLicense({
    eulaId: savingId,
    license: savingLicense
  })
    .then(result => {
      handleToastResponse("EULA Updated Successfully", result, true);
      eulas.value = eulas.value.map(e => {
        if (e.id === savingId) {
          return {
            ...e,
            license: savingLicense
          };
        }
        return e;
      });
      isEditing.value = false;
    })
    .catch(error => {
      handleToastResponse("Error Updating EULA", { error });
    })
    .finally(() => {
      removeArrayElement(savingEulaIds.value, savingId);
    });
};

const handleStartEditing = () => {
  isCreating.value = false;
  isEditing.value = true;
  nextTick(() => {
    editor.value?.quill.focus();
  });
};

const activateSelectedEula = () => {
  savingActivatedEula.value = true;
  const activated_on = Date.now();
  const savingId = selectedEulaId.value;
  activateEula({
    eulaId: savingId
  })
    .then(result => {
      handleToastResponse("EULA Activated Successfully", result, true);
      eulas.value = eulas.value.map(e => {
        if (e.id === savingId) {
          return {
            ...e,
            activated: true,
            activated_on
          };
        }
        return {
          ...e,
          activated: false
        };
      });
    })
    .catch(error => {
      handleToastResponse("Error Activating EULA", { error });
    })
    .finally(() => {
      savingActivatedEula.value = false;
    });
};

const deactivateSelectedEula = () => {
  savingActivatedEula.value = true;
  const deactivated_at = Date.now();
  const savingId = selectedEulaId.value;
  deactivateEula({
    eulaId: savingId
  })
    .then(result => {
      handleToastResponse("EULA Deactivated Successfully", result, true);
      eulas.value = eulas.value.map(e => {
        if (e.id === savingId) {
          return {
            ...e,
            activated: false,
            deactivated_at
          };
        }
        return {
          ...e
        };
      });
    })
    .catch(error => {
      handleToastResponse("Error Deactivating EULA", { error });
    })
    .finally(() => {
      savingActivatedEula.value = false;
    });
};

const handleActivateSelectedEula = () => {
  if (!canAlterSelectedEula.value) {
    return;
  }
  confirm.require({
    message: "Mark this EULA as active?",
    header: "Activate EULA",
    icon: "pi pi-exclamation-triangle",

    accept: activateSelectedEula
  });
};

const handleDeactivateSelectedEula = () => {
  confirm.require({
    message: "Deactivate this EULA?",
    header: "Deactivate EULA",
    icon: "pi pi-exclamation-triangle",

    accept: deactivateSelectedEula
  });
};

const handleSaveCreatingEula = () => {
  // todo: mutaiton to save
  if (isSaving.value || !selectedEula.value) {
    return;
  }
  isSaving.value = true;
  const license = editingSelectedEulaLicense.value;
  insertEula({
    license,
    activated: false
  })
    .then((result: { data?: { insert_eula_one?: Eula | null } }) => {
      const createdEula = result.data?.insert_eula_one as Eula;
      if (!createdEula) {
        return;
      }
      handleToastResponse("EULA created successfully", result, true);
      eulas.value.push(createdEula);
      isCreating.value = false;
      selectedEulaId.value = createdEula.id;
    })
    .catch(error => {
      handleToastResponse("Error creating EULA", { error });
    })
    .finally(() => {
      isSaving.value = false;
    });
};

const handleSave = () => {
  if (isSaving.value || !selectedEula.value) {
    return;
  }
  if (isCreating.value) {
    return handleSaveCreatingEula();
  } else {
    return handleUpdateSelectedEula();
  }
};

const handleCancel = () => {
  if (isEditing.value) {
    editingSelectedEulaLicense.value = selectedEula.value?.license || "";
    isEditing.value = false;
  } else if (isCreating.value) {
    isCreating.value = false;
  }
};

const exporting = ref(false);
const exportingError = ref("");
const exportEulas = async () => {
  if (exporting.value) {
    return;
  }
  exportingError.value = "";

  if (!activatedEula.value) {
    exportingError.value = "No activated EULA to export.";
    return;
  }
  exporting.value = true;

  try {
    await getAllUserEulaAcceptances();
  } catch (err) {
    exportingError.value =
      typeof err === "object" && err && "message" in err
        ? (err.message as string)
        : "Error when querying user EULA acceptances.";
    exporting.value = false;
    return;
  }

  const merges: Range[] = [];
  const rowProperties: RowInfo[] = [];

  const makeData = () => {
    const raw = allUserEulaAcceptances.value?.user_eula_acceptance;
    const retval: Array<string | undefined | null>[] = [];
    let currentEula = "";
    let currentRow = 0;

    if (raw && raw.length) {
      raw.forEach(e => {
        if (e.eula.created_at !== currentEula) {
          retval.push([
            `EULA Created: ${dateTimeFormat.format(new Date(e.eula.created_at))} ${e.eula.activated ? "(Active)" : ""}`,
            "",
            ""
          ]);
          rowProperties.push({ hpx: 30 });
          merges.push({
            s: { r: currentRow, c: 0 },
            e: { r: currentRow, c: 4 }
          });
          retval.push([
            "Customer Name",
            "Customer ID",
            "Customer Email",
            "Customer Signature Date"
          ]);
          rowProperties.push({ hpx: 20 });
          currentRow += 2;
          currentEula = e.eula.created_at;
        }

        retval.push([
          e.user.display_name,
          e.user.hal_identifier,
          e.user.email,
          dateTimeFormat.format(new Date(e.signed_at))
        ]);
        rowProperties.push({ hpx: 20 });
        currentRow += 1;
      });
    }
    return retval;
  };

  const data = makeData();
  const workbook: WorkBook = XLSX.utils.book_new();
  const worksheet: WorkSheet = XLSX.utils.aoa_to_sheet(data);

  worksheet["!cols"] = [{ wch: 35 }, { wch: 15 }, { wch: 35 }, { wch: 30 }];
  worksheet["!rows"] = rowProperties;
  worksheet["!merges"] = merges;

  const date = new Date(Date.now()).toLocaleDateString().replaceAll("/", "-");
  const fileName = `spyglass-EULA-customer-signatures-${date}.XLSX`;

  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet 1");
  XLSX.writeFile(workbook, fileName);
  exporting.value = false;
};
</script>

<style lang="scss" scoped>
.eula-export-btn {
  margin-left: 10px;
}
.subhead {
  width: 100%;
  background-color: var(--surface-200);
  padding: 20px 0;
  text-align: center;
}

#eula-list-items {
  list-style-type: none;
  padding-left: 0px;
  margin: 0px;
  li {
    background-color: var(--surface-100);
    border: 1px solid var(--border-main);
    border-width: 1px 0;
    padding-right: 10px;
    cursor: pointer;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;

    a {
      line-height: 2.5em;
      padding-left: 10px;
      color: var(--text-color);
      &:hover {
        text-decoration: underline;
      }
    }
    &.eula-selected {
      background-color: $highlightFocusBg;
      a {
        color: $gray-50;
        font-weight: 700;
      }
    }
  }
  li:nth-child(odd) {
    border-width: 0;

    &.eula-selected {
      background-color: $highlightFocusBg;
      a {
        color: $gray-50;
        font-weight: 700;
      }
    }
    background-color: var(--surface-25);
  }
}
.eula-container {
  display: flex;
  flex-direction: row;
  justify-content: center;
}
#eula-admin-wrapper {
  display: flex;
  flex-direction: row;
  width: 1200px;
  margin: 50px 0 0 0;
  #eula-list-wrapper {
    width: 350px;
    margin-right: 20px;
    display: flex;
    flex-direction: column;
    #eula-left-section {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      position: relative;
      h3 {
        margin-right: 5px;
      }
    }
  }
  #selected-eula {
    width: calc(100% - 150px);
  }
}
#selected-eula {
  display: flex;
  flex-direction: column;
  .selected-eula-actions {
    margin-bottom: 20px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
    align-items: center;
    .action-buttons {
      display: flex;
      flex-direction: row;
      button:not(:first-child) {
        margin-left: 10px;
      }
    }
  }
}
:deep(.p-editor-content) {
  .ql-editor {
    min-height: 400px;
  }
}
</style>
